import { useSyncExternalStore } from "react";

export type TouchLayoutPreference = "auto" | "on" | "off";

const STORAGE_KEY = "eclipse_touchLayout";
const COARSE = "(pointer: coarse)";
const listeners = new Set<() => void>();

const stored = (): TouchLayoutPreference => {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "on" || value === "off" ? value : "auto";
  } catch {
    return "auto";
  }
};

let preference = stored();

const coarse = () => typeof window !== "undefined" && (window.matchMedia?.(COARSE).matches ?? false);

const resolved = () => preference === "on" || (preference === "auto" && coarse());

// Note: the attribute drives the `touch:` variant, so the field screens' own classes follow the same answer as the hook.
const apply = () => {
  if (typeof document !== "undefined") document.documentElement.dataset.touch = resolved() ? "on" : "off";
  listeners.forEach(listener => listener());
};

if (typeof window !== "undefined") {
  window.matchMedia?.(COARSE).addEventListener?.("change", apply);
  apply();
}

export const setTouchLayout = (next: TouchLayoutPreference) => {
  preference = next;
  try {
    if (next === "auto") localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Note: without storage the choice still holds until the page reloads.
  }
  apply();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

// Note: MUI fields take their height from size, not from classes, so the touch layout picks the size.
export const useTouchFieldSize = (): "small" | "medium" => useSyncExternalStore(subscribe, resolved) ? "medium" : "small";

export const useTouchLayout = () => {
  const on = useSyncExternalStore(subscribe, resolved);
  const choice = useSyncExternalStore(subscribe, () => preference);
  return { on, preference: choice, set: setTouchLayout };
};
